import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useLocation, useNavigation, useNavigationType } from 'react-router';
import { cn } from '@rocco/utils/cn';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { Button } from '@rocco/components/button';
import { CategoryPageTileWithAdminActions } from './CategoryPageTileWithAdminActions';
import { useInfiniteScrollSentinel } from './hooks/useInfiniteScrollSentinel';
import { PRODUCTS_PER_PAGE } from './hooks/useProductViewMore';
import { ScrollReveal } from '~/ui-rocco/pages/home-page/components/ScrollReveal';
import { useIsBelowLg } from '~/ui-rocco/hooks/use-is-below-lg';
import type { DirectoryGridInjection } from '~/ui-rocco/pages/directory-browse/use-directory-grid-injections';
import { useDirectoryBrowseTheme } from '~/ui-rocco/pages/directory-browse/directory-browse-theme';

/** Desktop column counts the grid can render (the Display dropdown's range). */
export type DirectoryGridColumns = 2 | 3 | 4 | 5 | 6;

/** Column gap style — fixed per vertical, never varies with the column count. */
export type DirectoryGridGapVariant = 'standard' | 'projects';

const DEFAULT_INJECT_EVERY_ROWS = 3;

// The uniform browse page is a fixed 36-cell budget (BROWSE_PRODUCTS_PER_PAGE
// — the products a page fetches and the cells it spends them on). Cells are
// products, except these 1-based offsets within every 36-cell page, which
// take an amplifier (AAA) tile whenever the caller supplies any, cycling
// through the provided tiles. Amplifier cells spend cells from the budget
// rather than adding to it: the products they displace open the next page, so
// a category with no amplified professionals renders the same 36-cell page,
// just all products. 36 divides evenly by 2, 3, 4 and 6 columns, so pages end
// on a full row at those counts and infinite-scroll appends keep every page
// boundary clean (5 columns keeps its accepted ragged row, 36 % 5 = 1). The
// offsets mix parity and residues so the AAA cell drifts across columns at
// every column count (an even step would pin it to one column at 2-up). At
// 2/3/4/6 columns they never land on a module-injection boundary (multiples
// of 6, 9, 12 or 18 repeat identically within each 36-cell page); at 5
// columns (multiples of 15) the offset-21 cell of every fifth page does —
// cell 165 first — which is why the boundary check below runs after amplifier
// cells too.
const AMPLIFIER_PAGE_CELLS = 36;
const AMPLIFIER_CELL_OFFSETS = [8, 21, 34];

/** Whether the 1-based grid cell is one of the reserved amplifier offsets. */
const isAmplifierCell = (cell: number): boolean =>
    AMPLIFIER_CELL_OFFSETS.includes(((cell - 1) % AMPLIFIER_PAGE_CELLS) + 1);

// Tile reveal: each tile fades + rises a little as it scrolls into view, with a
// short per-column delay so a row cascades left→right (matching the design).
const REVEAL_STAGGER_MS = 40;

// Column classes per desktop column count. Tailwind needs the strings literal,
// so each count keeps its own full class string.
// Mobile columns are set per vertical: products stay 2-up (compare-and-shop
// density), while projects and professionals go single column so the imagery
// leads, Instagram-style. Desktop columns come from the page default or the
// user's Display selection. 5 and 6 columns only apply from lg — between md
// and lg the tiles would be under ~120px wide, so those selections clamp to 4.
const COL_CLASSES: Record<DirectoryGridColumns, string> = {
    2: 'grid-cols-2 md:grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    5: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-5',
    6: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-6',
};
const SINGLE_COL_CLASSES: Record<DirectoryGridColumns, string> = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-4 lg:grid-cols-5',
    6: 'grid-cols-1 md:grid-cols-4 lg:grid-cols-6',
};
// Figma: products/professionals use a 24px desktop column gap; projects use
// 22px. All use a 100px desktop row gap. The gap is keyed by vertical (not
// column count) so changing the Display selection never changes it.
const GAP_CLASSES: Record<DirectoryGridGapVariant, string> = {
    standard: 'gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-[6.25rem]',
    projects: 'gap-x-2 gap-y-12 md:gap-x-[1.375rem] md:gap-y-[6.25rem]',
};

/** Default skeleton tile aspect — the portrait tile most verticals use. */
const DEFAULT_SKELETON_ASPECT_CLASS = 'aspect-tile-portrait';

/**
 * Scroll position keyed by URL, restored on Back. `useProductViewMore` restores
 * the exact tiles the user saw (so page height is deterministic — tiles have a
 * fixed portrait aspect), but React Router's native restoration can fire before
 * the cached tiles finish laying out and land short. Saving/restoring here with
 * a double-RAF lands on the exact position instead.
 */
const gridScrollCache = new Map<string, number>();

/**
 * The search string without the `page` param (empty string when nothing
 * remains). View More rewrites `?page=N` via replaceState, so the scroll
 * key must stay stable across it; every other param is a different result
 * set and keys its own scroll position.
 */
const stripPageParam = (search: string): string => {
    const params = new URLSearchParams(search);
    params.delete('page');
    const remaining = params.toString();
    return remaining ? `?${remaining}` : '';
};

interface DirectoryUniformGridProps {
    /** All tiles currently loaded (skeleton + server + View More). */
    productTiles: TileViewModel<TileableModel>[];
    /**
     * Desktop grid columns (2–6) — the page default (4 for products and
     * professionals, 3 for projects) or the user's Display selection.
     */
    columns?: DirectoryGridColumns;
    /** Mobile columns below md. Products keep 2; projects/professionals use 1. */
    mobileColumns?: 1 | 2;
    /** Column gap style. Projects use a slightly narrower desktop column gap. */
    gapVariant?: DirectoryGridGapVariant;
    /**
     * Aspect class for skeleton tiles — must match the vertical's tile aspect
     * so page height stays deterministic for scroll restoration (projects are
     * landscape, everything else portrait).
     */
    skeletonAspectClass?: string;
    /** Plural noun for the end-of-list message (e.g. "products", "projects"). */
    entityNoun?: string;
    /** Tiles per deep-link page (skeleton tile count). Default 42 (products). */
    pageSize?: number;
    /** Total results across the catalogue (for the end-of-list message). */
    totalCount: number;
    /** Whether a View More fetch is in progress. */
    isLoading?: boolean;
    /** Whether more products remain to load. */
    hasMore?: boolean;
    /** Trigger loading the next page. */
    onLoadMore?: () => void;
    /** Error message from the last fetch, if any. */
    error?: string | null;
    /**
     * First page with loaded products (1-indexed). Pages below this render as
     * skeletons that lazy-load when scrolled into view (deep-link `?page=N`).
     */
    firstLoadedPage?: number;
    /** The page the user deep-linked to — scrolled to on mount. */
    requestedPage?: number;
    /** Lazy-load callback fired when a skeleton page enters the viewport. */
    onLoadSkeletonPage?: (pageNumber: number) => void;
    /** Pages currently loading (drives skeleton loading state). */
    loadingPages?: Set<number>;
    /** True on POP navigation — skip deep-link scroll, let the browser restore. */
    isRestoredFromCache?: boolean;
    /** Full-width modules cycled into the grid at the injection cadence. */
    injections?: DirectoryGridInjection[];
    /** Inject a module every N desktop rows (default 3). */
    injectEveryRows?: number;
    /**
     * Pre-rendered analytics-amplifier (AAA) tiles, cycled through the
     * amplifier cells of each 36-cell page — the uniform-grid equivalent of
     * the legacy grid's per-section amplifier slot. They spend cells out of
     * the page budget, so callers page products in BROWSE_PRODUCTS_PER_PAGE
     * (36) whether or not they have any: the products an amplifier cell
     * displaces open the next page instead of being dropped.
     */
    amplifierTiles?: ReactNode[];
    /**
     * Active category name (e.g. "Building"). When set, the end-of-list message
     * reads "…in {label}" with a Get Matched prompt.
     */
    activeCategoryLabel?: string;
    /** Opens the Get Matched flow from the end-of-list prompt. */
    onGetMatched?: () => void;
    /**
     * Hybrid load: gate infinite scroll behind an explicit first "Load More"
     * click. Page 1 shows the button with no auto-load; the first click loads
     * page 2 and switches into infinite scroll for the rest of the session.
     * Default false (infinite scroll active from the first batch).
     */
    hybridLoad?: boolean;
    /**
     * Crawlable `?page=N` URL for the Load More control. When set, the control
     * renders as a real `<a href>` (with a JS-intercepted click) so bots can
     * walk the item set without executing JS.
     */
    nextPageHref?: string;
}

/** Placeholder tile shown while a deep-link page lazy-loads. */
const SkeletonTile = ({ aspectClass }: { aspectClass: string }) => {
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const bar = cn(
        'animate-pulse rounded',
        isDark ? 'bg-white/10' : 'bg-gray-100',
    );
    return (
        <div className="flex flex-col gap-3">
            <div className={cn(aspectClass, 'w-full', bar)} />
            <div className={cn('h-4 w-3/4', bar)} />
            <div className={cn('h-3 w-1/2', bar)} />
        </div>
    );
};

interface SkeletonPageProps {
    pageNumber: number;
    onVisible?: (pageNumber: number) => void;
    colClasses: string;
    gapClasses: string;
    tileCount: number;
    aspectClass: string;
}

/**
 * A full page of skeleton tiles for a not-yet-loaded deep-link page. Fires
 * `onVisible` when it approaches the viewport (either scroll direction) so the
 * hook lazy-loads the real tiles for that page.
 */
const DirectoryUniformSkeletonPage = ({
    pageNumber,
    onVisible,
    colClasses,
    gapClasses,
    tileCount,
    aspectClass,
}: SkeletonPageProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const onVisibleRef = useRef(onVisible);
    onVisibleRef.current = onVisible;

    useEffect(() => {
        const node = ref.current;
        if (!node || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        onVisibleRef.current?.(pageNumber);
                    }
                }
            },
            { rootMargin: '1500px 0px 1500px 0px', threshold: 0 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [pageNumber]);

    return (
        <div ref={ref} className={pageNumber > 1 ? 'mt-20' : undefined}>
            <Grid colClasses={colClasses} rowsGapClass={gapClasses}>
                {Array.from({ length: tileCount }).map((_, index) => (
                    <SkeletonTile key={index} aspectClass={aspectClass} />
                ))}
            </Grid>
        </div>
    );
};

/**
 * Uniform portrait product grid for the 5.0 directory browse pages. Renders the
 * same admin/follow-enabled tiles as the legacy category grid (via
 * `CategoryPageTileWithAdminActions`) in a flat responsive grid — no featured
 * A/B sections — matching the Figma browse design.
 *
 * All the paging machinery is owned by `useProductViewMore` in the caller; this
 * component only presents its output: infinite-scroll via the shared sentinel,
 * deep-link skeleton pages (`?page=N`) that lazy-load on scroll, full-width
 * injected modules cycled at a fixed row cadence, and single-cell amplifier
 * (AAA) tiles at reserved offsets within each 36-cell page. POP scroll
 * restoration works because tiles have a fixed portrait aspect, so page
 * height is deterministic.
 */
export const DirectoryUniformGrid = React.memo(
    ({
        productTiles,
        columns = 4,
        mobileColumns = 2,
        gapVariant = 'standard',
        skeletonAspectClass = DEFAULT_SKELETON_ASPECT_CLASS,
        entityNoun = 'products',
        pageSize = PRODUCTS_PER_PAGE,
        totalCount,
        isLoading = false,
        hasMore = false,
        onLoadMore,
        error,
        firstLoadedPage = 1,
        requestedPage = 1,
        onLoadSkeletonPage,
        isRestoredFromCache = false,
        injections,
        injectEveryRows = DEFAULT_INJECT_EVERY_ROWS,
        amplifierTiles,
        activeCategoryLabel,
        onGetMatched,
        hybridLoad = false,
        nextPageHref,
    }: DirectoryUniformGridProps) => {
        const navigationType = useNavigationType();
        const location = useLocation();
        const navigation = useNavigation();
        // A same-vertical navigation is loading (filter apply, sort change,
        // category switch): the current tiles are about to be replaced. Dim
        // them so the tap reads as acknowledged instead of leaving the stale
        // grid frozen for the loader round trip.
        const isPendingResults =
            navigation.state === 'loading' &&
            navigation.location.pathname.split('/')[1] ===
                location.pathname.split('/')[1];
        const isDark = useDirectoryBrowseTheme() === 'dark';

        const colClasses = (
            mobileColumns === 1 ? SINGLE_COL_CLASSES : COL_CLASSES
        )[columns];
        const gapClasses = GAP_CLASSES[gapVariant];
        // Between md and lg, 5-6 column selections render 4 columns (see
        // COL_CLASSES) — cadence and stagger must follow the RENDERED count
        // or injections land mid-row on tablets.
        const isBelowLg = useIsBelowLg();
        const renderedColumns = isBelowLg && columns >= 5 ? 4 : columns;
        // At 5-6 columns the 22px browse title truncates almost immediately —
        // step the tile typography down one size. The tiles scope the compact
        // classes to lg+, matching where 5-6 columns actually render.
        const compactBrowseText = columns >= 5;
        // Column-aware image sizes: tell the browser the real rendered tile
        // width (~100/columns vw on desktop) so the srcset serves sharper
        // files at 2-3 columns and smaller ones at 5-6. Below md the width is
        // set by the vertical's fixed mobile column count instead. 5-6 column
        // selections clamp to 4 between md and lg (see COL_CLASSES), so their
        // sizes must say 25vw there or tablets fetch an undersized file. The
        // page content is capped at 120rem (1920px), so beyond that the tile
        // width is a fixed pixel value, not a vw share — without the cap entry
        // wide displays would over-fetch ever-larger files.
        const cappedTilePx = Math.round(
            (1920 - 96 - 24 * (columns - 1)) / columns,
        );
        const desktopTileVw = `${Math.round(100 / columns)}vw`;
        const mobileTileVw = mobileColumns === 1 ? '100vw' : '49vw';
        const tileImageSizes =
            columns >= 5
                ? `(min-width: 1920px) ${cappedTilePx}px, (min-width: 1024px) ${desktopTileVw}, (min-width: 768px) 25vw, ${mobileTileVw}`
                : `(min-width: 1920px) ${cappedTilePx}px, (min-width: 768px) ${desktopTileVw}, ${mobileTileVw}`;

        // Capture the cache-restore flag once, at mount. It's a navigation-time
        // fact, but the prop is unstable: on an initial load `navigationType` is
        // 'POP', so once `useProductViewMore` persists its cache the prop flips
        // false→true. Reading it live would flip tiles to `immediate` mid-session
        // and freeze the reveal. The ref pins the mount value.
        const restoredFromCache = useRef(isRestoredFromCache).current;

        // Hybrid load: infinite scroll stays off until the user clicks Load More
        // once. It starts off on every fresh result set — SSR + hydration render
        // the crawlable anchor, not the sentinel — and resets whenever the
        // category / filters / sort change so the new set needs an explicit Load
        // More again. Only meaningful when hybridLoad is set; otherwise infinite
        // scroll is always active (legacy behaviour).
        const [infiniteEnabled, setInfiniteEnabled] = useState(false);
        const infiniteScrollActive = hybridLoad ? infiniteEnabled : true;

        // Result-set identity: category + filters + sort as reflected in the URL,
        // with `?page=N` stripped. View More rewrites the page param via
        // replaceState, which does NOT update the router location, so this key is
        // stable through infinite scroll and only changes on a real filter/sort/
        // category (or POP) navigation.
        const filterNavKey = useMemo(() => {
            const params = new URLSearchParams(location.search);
            params.delete('page');
            return `${location.pathname}?${params.toString()}`;
        }, [location.pathname, location.search]);
        useEffect(() => {
            // New result set → gate infinite scroll behind an explicit Load More
            // again. No-op on mount (already false); never fires during infinite
            // scroll (filterNavKey is stable there).
            setInfiniteEnabled(false);
        }, [filterNavKey]);

        const handleLoadMore = useCallback(() => {
            if (hybridLoad) setInfiniteEnabled(true);
            onLoadMore?.();
        }, [hybridLoad, onLoadMore]);

        // The Load More control is a real anchor (crawlable). Intercept plain
        // left-clicks to load client-side and switch into infinite mode; let
        // modified clicks (open-in-new-tab etc.) follow the href to the real
        // `?page=N` URL.
        const handleAnchorClick = useCallback(
            (event: React.MouseEvent<HTMLAnchorElement>) => {
                if (
                    event.defaultPrevented ||
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                ) {
                    return;
                }
                event.preventDefault();
                handleLoadMore();
            },
            [handleLoadMore],
        );

        // Persist the live scroll position (saved on unmount, i.e. navigating
        // away) so Back can restore it. The key carries the filter/sort params
        // too — an applied filter refreshes the whole result set, so its depth
        // is a different position from the unfiltered page's, and the key
        // change is what drives the scroll-to-top below. Only `?page=N`
        // is stripped: View More rewrites it via replaceState, and the same
        // grid instance must save and restore under one stable key.
        const scrollKey = location.pathname + stripPageParam(location.search);
        const lastScrollYRef = useRef(0);
        // Scroll positions are cached per column count as well as per URL: a
        // saved offset is only meaningful at the layout it was measured in
        // (6-col pages are roughly half the height of 3-col ones). After a
        // Display change, restores for the old count simply miss — landing at
        // the top — instead of re-asserting an unreachable offset for 2.5s.
        const columnsRef = useRef(columns);
        columnsRef.current = columns;
        const scrollCacheKey = `${scrollKey}::c${columns}`;

        // The key the continuous save is *committed* to. It only advances to a
        // new category once the scroll-to-top / POP restore for that navigation
        // has run (see the scrollKey effect below). Between a category PUSH and
        // that scroll adjustment, window.location.pathname already reads the new
        // category while window.scrollY still holds the previous one's depth —
        // saving under the live pathname there would cache the old depth against
        // the new category and Back would restore to the wrong offset.
        const saveKeyRef = useRef(scrollKey);
        useEffect(() => {
            const onScroll = () => {
                lastScrollYRef.current = window.scrollY;
                // Only save once the live URL matches the committed key, so an
                // in-flight (momentum) scroll during a category or filter
                // switch can't mis-attribute the outgoing page's depth to the
                // incoming one.
                const liveKey =
                    window.location.pathname +
                    stripPageParam(window.location.search);
                if (liveKey !== saveKeyRef.current) return;
                gridScrollCache.set(
                    `${saveKeyRef.current}::c${columnsRef.current}`,
                    window.scrollY,
                );
            };
            window.addEventListener('scroll', onScroll, { passive: true });
            return () => window.removeEventListener('scroll', onScroll);
        }, []);

        // Own scroll restoration for this route. The route opts out of React
        // Router's <ScrollRestoration>, which otherwise leaves the browser on
        // `auto` — so on Back, while the loader refetches and the grid is briefly
        // absent, the browser clamps the scroll to the short page's max (a visible
        // jump to a wrong position). Set `manual` while the grid is mounted so it
        // can restore the exact position itself once its tiles are back in the
        // DOM, and restore the previous value on unmount so navigating away
        // doesn't leave the rest of the app on manual scroll restoration.
        useEffect(() => {
            if (typeof window === 'undefined' || !window.history) return;
            const previous = window.history.scrollRestoration;
            window.history.scrollRestoration = 'manual';
            return () => {
                window.history.scrollRestoration = previous;
            };
        }, []);
        // (The unmount-time cache save that lived here is gone: it was racy
        // against the category-change scroll-to-top, which can fire
        // before unmount-ordered cleanups and poison the saved position with
        // 0. The continuous per-scroll save above always holds the latest
        // position under the correct key.)

        // Category or filter/sort navigation does NOT remount this grid (same
        // route, same element), so the mount-time restore below never re-runs
        // for it. A category or filter PUSH scrolls to the top — the result
        // set is new, and a viewport buried four screens deep would miss
        // everything above it — so POP needs an explicit restore here. Skips
        // the first run: mount is the effect below's job (detail-page → Back,
        // deep links).
        const firstKeyRunRef = useRef(true);
        useEffect(() => {
            if (firstKeyRunRef.current) {
                firstKeyRunRef.current = false;
                return;
            }
            if (navigationType === 'POP') {
                const savedY = gridScrollCache.get(scrollCacheKey);
                if (savedY != null) {
                    window.scrollTo({ top: savedY, behavior: 'instant' });
                }
            } else {
                window.scrollTo(0, 0);
            }
            // Commit the continuous save to the new category only now — after the
            // scroll has been positioned — so the onScroll guard suppressed any
            // momentum scroll that fired mid-switch (while the pathname read the
            // new category but the scroll still held the old depth).
            saveKeyRef.current = scrollKey;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [scrollKey]);

        // Skeleton pages to render above the loaded products (deep-link `?page=N`).
        const skeletonPages = useMemo(() => {
            const pages: number[] = [];
            for (let page = 1; page < firstLoadedPage; page++) {
                pages.push(page);
            }
            return pages;
        }, [firstLoadedPage]);

        // On mount, position the scroll once.
        const loadedGridRef = useRef<HTMLDivElement>(null);
        const hasScrolledRef = useRef(false);
        useLayoutEffect(() => {
            if (hasScrolledRef.current) return;

            // Back/Forward (POP): restore the saved scroll position. This keys off
            // gridScrollCache (scroll position), NOT restoredFromCache (tile cache):
            // a Back triggers a loader revalidation, during which the grid streams
            // its tiles in (short page → full height over ~400ms) and can briefly
            // read a viewMoreCache miss — but the saved position is still valid.
            const savedY =
                navigationType === 'POP'
                    ? gridScrollCache.get(scrollCacheKey)
                    : undefined;
            if (savedY != null) {
                hasScrolledRef.current = true;
                // Re-assert the scroll each frame until we actually land on savedY.
                // A single scrollTo here fires before the revalidated tiles are in
                // the DOM, so the page is still too short and the browser clamps us
                // to the top — and the one-shot never re-fires. Retry until the page
                // has grown tall enough to reach savedY (or we time out), and bail
                // the moment the user takes over scrolling so we never fight them.
                let raf = 0;
                const deadline = performance.now() + 2500;
                let userTookOver = false;
                const onUserScroll = () => {
                    userTookOver = true;
                };
                const cleanupListeners = () => {
                    window.removeEventListener('wheel', onUserScroll);
                    window.removeEventListener('touchmove', onUserScroll);
                };
                const restore = () => {
                    window.scrollTo({ top: savedY, behavior: 'instant' });
                    const landed = Math.abs(window.scrollY - savedY) <= 2;
                    if (
                        !landed &&
                        !userTookOver &&
                        performance.now() < deadline
                    ) {
                        raf = requestAnimationFrame(restore);
                    } else {
                        cleanupListeners();
                    }
                };
                window.addEventListener('wheel', onUserScroll, {
                    passive: true,
                });
                window.addEventListener('touchmove', onUserScroll, {
                    passive: true,
                });
                restore();
                return () => {
                    cancelAnimationFrame(raf);
                    cleanupListeners();
                };
            }

            // Deep-link (`?page=N`) FRESH navigation: no saved scroll position, so
            // scroll to the loaded grid, which starts at the requested page. Back
            // navigations never reach here — they carry a saved position (a fresh
            // page load is also 'POP', but has no saved position, so it lands here).
            if (requestedPage > 1 && loadedGridRef.current) {
                hasScrolledRef.current = true;
                let innerRaf = 0;
                const outerRaf = requestAnimationFrame(() => {
                    innerRaf = requestAnimationFrame(() => {
                        loadedGridRef.current?.scrollIntoView({
                            behavior: 'instant',
                            block: 'start',
                        });
                    });
                });
                return () => {
                    cancelAnimationFrame(outerRaf);
                    cancelAnimationFrame(innerRaf);
                };
            }
            // Run once on mount; inputs are stable for this instance.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const sentinelRef = useInfiniteScrollSentinel({
            enabled: infiniteScrollActive && hasMore && !isLoading,
            onTrigger: () => onLoadMore?.(),
        });

        // Interleave the loaded tiles with full-width injected modules and
        // single-cell amplifier (AAA) tiles. The module cadence (every N
        // desktop rows) divides the 36-cell page evenly so an injection lands
        // near each page boundary — the extra height buys time for the next
        // infinite-scroll page to arrive. Positions count every cell
        // (products + amplifiers), so module injections stay on row
        // boundaries as amplifier cells shift the flow.
        const injectEveryTiles = injectEveryRows * renderedColumns;

        // Amplifier cells spend cells the products would otherwise fill, so a
        // page's 36 fetched products cover a 36-cell page (33 of them) and
        // open the next one (the other 3). While more products are still to
        // come, render whole pages only: the surplus is the head of the next
        // page, and drawing it early would leave the last row short until that
        // page arrives. Once everything is loaded there is nothing to wait
        // for, so the surplus renders with the tail. Grids without amplifier
        // tiles spend the budget on products alone and hold nothing back.
        const amplifierCellsPerPage = amplifierTiles?.length
            ? AMPLIFIER_CELL_OFFSETS.length
            : 0;
        const productsPerPage = AMPLIFIER_PAGE_CELLS - amplifierCellsPerPage;
        const wholePages = Math.floor(productTiles.length / productsPerPage);
        const visibleTiles =
            amplifierCellsPerPage > 0 && hasMore && wholePages > 0
                ? productTiles.slice(0, wholePages * productsPerPage)
                : productTiles;

        const items: ReactNode[] = [];
        let injectionIndex = 0;
        let amplifierIndex = 0;
        let cellCount = 0;

        // Push the full-width module when the cell just added completed an
        // injection row. Runs after every cell (product or amplifier) so a
        // boundary can never be skipped over. Skips the injection after the
        // very last loaded tile — a boundary injection lands on the last tile
        // of each page, so before Load More (and at the tail during infinite
        // scroll) it would dangle at the end of the grid with no row beneath
        // it, above the button / end message. Once more tiles load it's no
        // longer the last tile and the injection takes its proper
        // between-rows place.
        const pushBoundaryInjection = (isLastLoadedTile: boolean) => {
            const atInjectPoint =
                cellCount % injectEveryTiles === 0 && !isLastLoadedTile;
            const injection =
                atInjectPoint && injections && injections.length > 0
                    ? injections[injectionIndex % injections.length]
                    : undefined;
            if (!injection) {
                return;
            }
            // Key by cycle count, not tile index: the tile index shifts
            // when the Display column count changes, and an index-based key
            // would remount every injected module (resetting carousels and
            // re-firing impressions) on a pure layout change.
            const cycleIndex = injectionIndex;
            injectionIndex += 1;
            items.push(
                // Design gives the injected module 160px below it before the
                // next row; the grid row gap already contributes 100px, so add
                // the remaining 60px here.
                <div
                    key={`${injection.key}-${cycleIndex}`}
                    className="col-span-full md:pb-[3.75rem]"
                >
                    {injection.node}
                </div>,
            );
        };

        visibleTiles.forEach((tile, index) => {
            const isLastLoadedTile = index === visibleTiles.length - 1;

            // Amplifier (AAA) cells occupy their fixed offsets within each
            // 36-cell page. Inserted ahead of the product heading for that
            // cell, so an amplifier only ever renders with a product after it
            // — it can't dangle at the grid tail while the next page loads.
            while (amplifierTiles?.length && isAmplifierCell(cellCount + 1)) {
                const amplifierTile =
                    amplifierTiles[amplifierIndex % amplifierTiles.length];
                cellCount += 1;
                items.push(
                    <ScrollReveal
                        key={`amplifier-${amplifierIndex}`}
                        durationClassName="duration-300"
                        hiddenClassName="translate-y-2 opacity-0"
                        delay={
                            ((cellCount - 1) % renderedColumns) *
                            REVEAL_STAGGER_MS
                        }
                        // Stretch to the row height and square the tile's
                        // baked-in corner radius to match the browse grid's
                        // square-cornered tiles.
                        className="h-full [&>a]:rounded-none"
                        immediate={restoredFromCache}
                    >
                        {amplifierTile}
                    </ScrollReveal>,
                );
                amplifierIndex += 1;
                // Load-bearing at 5 columns: the offset-21 cell of every
                // fifth page completes an injection row (see the constants
                // above), so the module must fire here, after the amplifier.
                pushBoundaryInjection(false);
            }

            cellCount += 1;
            items.push(
                <ScrollReveal
                    key={tile.model.id}
                    durationClassName="duration-300"
                    hiddenClassName="translate-y-2 opacity-0"
                    delay={
                        ((cellCount - 1) % renderedColumns) * REVEAL_STAGGER_MS
                    }
                    // On Back, tiles are restored from cache — show them at once
                    // so the reveal never fights scroll restoration. Fresh
                    // filter/category results replay the reveal: the dim during
                    // the loader round trip acknowledges the tap, and the
                    // staggered reveal marks the new set arriving — matching the
                    // initial-load choreography. Tiles present in both result
                    // sets keep their key (and stay revealed) rather than
                    // blinking out and back in.
                    immediate={restoredFromCache}
                >
                    <CategoryPageTileWithAdminActions
                        tile={{
                            ...tile,
                            imageSizes: tileImageSizes,
                            compactBrowseText,
                        }}
                    />
                </ScrollReveal>,
            );
            pushBoundaryInjection(isLastLoadedTile);
        });

        const loadMoreButtonColor = isDark ? 'outline' : 'dark';
        const loadMoreButtonClass = cn(
            'min-w-[10.625rem] border transition-colors duration-200',
            isDark
                ? 'border-white bg-transparent text-white hover:bg-white hover:text-[#181818]'
                : 'border-black hover:bg-white hover:text-black',
        );
        // In hybrid mode the button is only for the first (page N → N+1) step:
        // once infinite scroll takes over it's gone. Non-hybrid keeps it beside
        // the always-active sentinel (legacy behaviour).
        const showLoadMoreButton = !hybridLoad || !infiniteEnabled;

        return (
            <div
                className={cn(
                    'px-4 pb-[6.25rem] transition-opacity duration-200 md:px-12',
                    isPendingResults &&
                        'pointer-events-none animate-pulse opacity-40',
                )}
                style={{ overflowAnchor: 'none' }}
            >
                {skeletonPages.map((pageNumber) => (
                    <DirectoryUniformSkeletonPage
                        key={`skeleton-page-${pageNumber}`}
                        pageNumber={pageNumber}
                        onVisible={onLoadSkeletonPage}
                        colClasses={colClasses}
                        gapClasses={gapClasses}
                        // A page spends its whole budget on cells whether or
                        // not amplifier tiles take some of them, so one page
                        // size of skeletons matches the rows it stands in for
                        // and deep-link scroll targets stay put.
                        tileCount={pageSize}
                        aspectClass={skeletonAspectClass}
                    />
                ))}

                <div ref={loadedGridRef}>
                    <Grid colClasses={colClasses} rowsGapClass={gapClasses}>
                        {items}
                    </Grid>
                </div>

                {error && (
                    <div
                        className="mt-10 text-center text-base text-red-600"
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                {hasMore ? (
                    <>
                        {/* Infinite-scroll sentinel: only mounted once infinite
                        mode is active (always active when hybridLoad is off). */}
                        {infiniteScrollActive && (
                            <div
                                ref={sentinelRef}
                                aria-hidden="true"
                                className="h-px w-full"
                            />
                        )}
                        {showLoadMoreButton && onLoadMore && (
                            <div className="flex justify-center pt-20">
                                {nextPageHref ? (
                                    <Button
                                        asChild
                                        color={loadMoreButtonColor}
                                        size="larger"
                                        className={loadMoreButtonClass}
                                    >
                                        <a
                                            href={nextPageHref}
                                            onClick={handleAnchorClick}
                                            aria-label={`Load more ${entityNoun}`}
                                        >
                                            View More
                                        </a>
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleLoadMore}
                                        disabled={isLoading}
                                        color={loadMoreButtonColor}
                                        size="larger"
                                        className={loadMoreButtonClass}
                                        aria-label={`Load more ${entityNoun}`}
                                    >
                                        View More
                                    </Button>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    productTiles.length > 0 && (
                        <div className="pt-[6.25rem]">
                            <hr
                                className={cn(
                                    'm-0 border-t',
                                    isDark
                                        ? 'border-white/15'
                                        : 'border-[#1f1f21]/15',
                                )}
                            />
                            <p
                                className={cn(
                                    'pt-10 text-center text-base',
                                    isDark ? 'text-white/60' : 'text-[#767676]',
                                )}
                            >
                                {activeCategoryLabel ? (
                                    <>
                                        Showing all{' '}
                                        {totalCount.toLocaleString()}{' '}
                                        {entityNoun}
                                        in {activeCategoryLabel}. Can&rsquo;t
                                        find what you&rsquo;re looking for?{' '}
                                        <button
                                            type="button"
                                            onClick={onGetMatched}
                                            className={cn(
                                                'underline underline-offset-2 hover:no-underline',
                                                isDark
                                                    ? 'text-white'
                                                    : 'text-[#181818]',
                                            )}
                                        >
                                            Try Get Matched
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        Showing all{' '}
                                        {totalCount.toLocaleString()}{' '}
                                        {entityNoun}
                                    </>
                                )}
                            </p>
                        </div>
                    )
                )}
            </div>
        );
    },
);

DirectoryUniformGrid.displayName = 'DirectoryUniformGrid';
